import { useParams, Link, useNavigate } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Seo } from "../components/Seo";
import { Footer } from "../components/Footer";
import { EnquiryForm } from "../components/EnquiryForm";
import { findDestination } from "../data/destinations";
import { MapPin, CalendarDays, Sparkles, ArrowLeft, Star, Clock, Wallet, Compass, Lightbulb } from "lucide-react";

export default function DestinationGuide() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const d = findDestination(slug);

  if (!d) {
    return (
      <div className="min-h-screen grain">
        <Seo title="Guide not found" description="This destination guide could not be found." path={`/destinations/${slug}`} noindex />
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <p className="font-display text-2xl mb-2">Guide not found</p>
          <Link to="/destinations" className="text-[#C8553D] underline underline-offset-4">Browse all destinations</Link>
        </div>
      </div>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: `${d.name}, ${d.country} Travel Guide`,
    description: d.intro,
    image: d.image,
    datePublished: d.published || d.updated,
    dateModified: d.updated,
    author: { "@type": "Person", name: "Gurman Soni", url: "https://wanderlyme.com/about" },
    publisher: { "@type": "Organization", name: "WanderlyMe", url: "https://wanderlyme.com/", logo: { "@type": "ImageObject", url: "https://wanderlyme.com/og-default.jpg" } },
    mainEntityOfPage: `https://wanderlyme.com/destinations/${d.slug}`,
    about: { "@type": "TouristDestination", name: `${d.name}, ${d.country}` },
  };

  const facts = [
    { icon: Clock, label: "Trip length", value: d.tripLength },
    { icon: CalendarDays, label: "Best time", value: d.bestTime },
    { icon: Wallet, label: "Mid-range budget", value: d.budget.mid },
  ];

  return (
    <div className="min-h-screen grain relative" data-testid="destination-guide">
      <Seo
        title={`${d.name}, ${d.country} Travel Guide`}
        description={`${d.tagline}. Best time to visit ${d.name}: ${d.bestTime}. Recommended trip length ${d.tripLength}. Neighbourhoods, top attractions, budget and a sample day-by-day itinerary — then build your own with WanderlyMe.`}
        path={`/destinations/${d.slug}`}
        image={d.image}
        type="article"
        jsonLd={jsonLd}
      />
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 py-12">
        <Link to="/destinations" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-[#C8553D] transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" /> All destinations
        </Link>

        <div className="relative overflow-hidden rounded-3xl border border-black/5 mb-8">
          <img src={d.image} alt={`${d.name}, ${d.country}`} className="w-full h-72 sm:h-96 object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <div className="absolute bottom-0 p-6 sm:p-8 text-[#FDFBF7]">
            <p className="text-xs uppercase tracking-widest opacity-80 mb-2">{d.country} · {d.region}</p>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight">{d.name}</h1>
            <p className="mt-2 text-base sm:text-lg opacity-90">{d.tagline}</p>
          </div>
        </div>

        <p className="text-xs text-muted-foreground mb-6" data-testid="guide-last-updated">Last updated: {new Date(d.updated).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })}</p>
        {/* Quick facts */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-10">
          {facts.map((f) => (
            <div key={f.label} className="flex items-start gap-3 p-4 rounded-2xl border border-black/5 bg-white/60">
              <f.icon className="w-5 h-5 text-[#C8553D] shrink-0 mt-0.5" strokeWidth={1.75} />
              <div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground">{f.label}</p>
                <p className="font-medium text-sm mt-0.5">{f.value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Overview */}
        <h2 className="font-display text-3xl mb-4">Overview</h2>
        <p className="text-lg leading-relaxed text-foreground/90 mb-12">{d.intro}</p>

        {/* Neighbourhoods */}
        <h2 className="font-display text-3xl mb-6 flex items-center gap-3"><Compass className="w-6 h-6 text-[#C8553D]" /> Where to stay & explore</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-12">
          {d.areas.map((a, i) => (
            <div key={i} className="p-6 rounded-2xl border border-black/5 bg-white/60">
              <h3 className="font-display text-xl mb-1">{a.name}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{a.detail}</p>
            </div>
          ))}
        </div>

        {/* Highlights */}
        <h2 className="font-display text-3xl mb-4 flex items-center gap-3"><Star className="w-6 h-6 text-[#C8553D]" /> Top attractions</h2>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-12">
          {d.highlights.map((h, i) => (
            <li key={i} className="flex gap-2 items-start p-4 rounded-xl border border-black/5 bg-white/60">
              <MapPin className="w-4 h-4 text-[#1A3B2B] shrink-0 mt-1" /> <span>{h}</span>
            </li>
          ))}
        </ul>

        {/* Itinerary */}
        <h2 className="font-display text-3xl mb-6 flex items-center gap-3"><CalendarDays className="w-6 h-6 text-[#1A3B2B]" /> Sample itinerary</h2>
        <div className="space-y-4 mb-12">
          {d.itinerary.map((it) => (
            <div key={it.day} className="flex gap-4 p-6 rounded-2xl border border-black/5 bg-white/60">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#1A3B2B] text-[#FDFBF7] font-display text-xl shrink-0">{it.day}</div>
              <div>
                <h3 className="font-display text-xl">{it.title}</h3>
                <p className="text-foreground/90 mt-1">{it.detail}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Budget */}
        <h2 className="font-display text-3xl mb-6 flex items-center gap-3"><Wallet className="w-6 h-6 text-[#C8553D]" /> Budget guide</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-3">
          {[
            { tier: "Budget", value: d.budget.budget, note: "Hostels, street food, public transport" },
            { tier: "Mid-range", value: d.budget.mid, note: "Boutique stays, restaurants, some tours" },
            { tier: "Luxury", value: d.budget.luxury, note: "Top hotels, fine dining, private guides" },
          ].map((b) => (
            <div key={b.tier} className="p-6 rounded-2xl border border-black/5 bg-white/60">
              <p className="text-xs uppercase tracking-widest text-[#C8553D] mb-1">{b.tier}</p>
              <p className="font-display text-2xl">{b.value}</p>
              <p className="text-muted-foreground text-sm mt-2">{b.note}</p>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mb-12">Approximate spend per person, per day, excluding flights.</p>

        {/* Tips */}
        <h2 className="font-display text-3xl mb-6 flex items-center gap-3"><Lightbulb className="w-6 h-6 text-[#1A3B2B]" /> Practical travel tips</h2>
        <ul className="space-y-3 mb-14">
          {d.tips.map((t, i) => (
            <li key={i} className="flex gap-3 items-start p-4 rounded-xl border border-black/5 bg-white/60">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#F3EFE8] text-[#C8553D] text-sm font-medium shrink-0">{i + 1}</span>
              <span className="text-foreground/90">{t}</span>
            </li>
          ))}
        </ul>

        {/* CTA */}
        <div className="rounded-3xl bg-[#1A3B2B] text-[#FDFBF7] p-8 sm:p-10 text-center">
          <h2 className="font-display text-3xl mb-3">Plan your own {d.name} trip</h2>
          <p className="opacity-85 mb-6 max-w-xl mx-auto">Turn this guide into a personalised {d.tripLength} itinerary — with hotels, restaurants and hidden gems matched to your exact budget, in seconds.</p>
          <button
            data-testid="guide-plan-cta"
            onClick={() => navigate("/plan")}
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#C8553D] text-[#FDFBF7] hover:bg-[#a8412f] transition-colors"
          >
            <Sparkles className="w-4 h-4" /> Build my {d.name} plan
          </button>
        </div>
        <div className="mt-8">
          <EnquiryForm destination={`${d.name}, ${d.country}`} heading={`Plan ${d.name} with a human`} />
        </div>
        <p className="mt-8 text-sm text-muted-foreground text-center" data-testid="guide-how-it-works">
          New to WanderlyMe? <Link to="/features" className="text-[#C8553D] underline underline-offset-4">See how it works</Link> — plan together, smart daily routes, every place verified against Google.
        </p>
      </div>
      <Footer />
    </div>
  );
}
